import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { BottomSheet } from "../../../shared/components/BottomSheet.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { Badge } from "../../../shared/components/Badge.jsx";
import { formatAppDate } from "../../../shared/utils/dateUtils.js";
import { openDocumentPdf, shareDocumentPdf } from "../../../shared/utils/pdfShare.js";
import { useInvoices } from "../../invoices/hooks/useInvoices.js";
import { usePurchases } from "../../purchases/hooks/usePurchases.js";
import { useActiveCompany } from "../../../shared/context/ActiveCompanyContext.jsx";
import { openingFor } from "../models/customer.js";
import { getPartyPayments } from "../../invoices/services/paymentService.js";
import { PaymentReceiptModal } from "../../invoices/components/PaymentReceiptModal.jsx";
import { PaymentCorrectionHost } from "../../invoices/components/PaymentCorrection.jsx";
import { CustomerInvoicesPanel } from "./CustomerInvoicesModal.jsx";
import { buildLedger, filterPartyBills } from "../utils/ledger.js";
import { financialYearRange } from "../../../shared/utils/dateRanges.js";

const TABS = [
  { key: "ledger", label: "Ledger" },
  { key: "payments", label: "Payments" },
  { key: "invoices", label: "Invoices" }
];
const RANGES = [
  { key: "thisFY", label: "This FY" },
  { key: "lastFY", label: "Last FY" },
  { key: "all", label: "All time" },
  { key: "custom", label: "Custom" }
];

const money = (n) => `₹${Math.abs(Number(n || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function balanceLabel(value, mode) {
  if (Math.abs(value) < 0.005) return "Settled";
  if (mode === "purchases") return value > 0 ? "Payable" : "Advance paid";
  return value > 0 ? "Due" : "Advance";
}

function rowParticulars(row, mode) {
  if (row.kind === "bill") return `${mode === "purchases" ? "Bill" : "Invoice"} ${row.ref}`;
  if (row.kind === "paidAtBilling") return `Paid at billing — ${row.ref}`;
  return `${mode === "purchases" ? "Payment made" : "Payment"} ${row.ref}${row.mode ? ` (${row.mode})` : ""}`;
}

/**
 * Party ledger: Ledger (bills + payments with running balance, PDF), Payments (receipts with
 * edit/void) and Invoices tabs. Customers use sales invoices; vendors use purchase bills.
 */
export function CustomerLedgerModal({ isOpen, onClose, customer, initialTab = "ledger" }) {
  const navigate = useNavigate();
  const { invoices, refetch: refetchInvoices } = useInvoices();
  const { purchases, refetch: refetchPurchases } = usePurchases();
  // The working company (header): its letterhead, and only its bills, receipts and opening due
  const { activeCompany: defaultCompany, activeCompanyId, ownsRecord, defaultCompany: mainCompany } = useActiveCompany();

  const partyType = customer?.partyType || "customer";
  const canSales = partyType !== "vendor";
  const canPurchases = partyType === "vendor" || partyType === "both";

  const [tab, setTab] = useState(initialTab);
  const [mode, setMode] = useState(canSales ? "sales" : "purchases");
  const [rangeKey, setRangeKey] = useState("thisFY");
  const [custom, setCustom] = useState(() => financialYearRange(0));
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [editPayment, setEditPayment] = useState(null);
  const [voidingPayment, setVoidingPayment] = useState(null);
  const [pdfBusy, setPdfBusy] = useState("");
  const ledgerRef = useRef(null);

  const loadPayments = useCallback(async () => {
    if (!customer?.id) return;
    setLoadingPayments(true);
    try {
      const all = await getPartyPayments(customer.id);
      // Receipts without a company were made before companies were tracked: they count for the default company
      setPayments(all.filter((p) => (p.companyId ? p.companyId === activeCompanyId : activeCompanyId === mainCompany?.id)));
    } catch (err) {
      console.error("Failed to load party payments:", err);
    } finally {
      setLoadingPayments(false);
    }
  }, [customer?.id, activeCompanyId, mainCompany?.id]);

  useEffect(() => {
    if (!isOpen) return;
    setTab(initialTab);
    setMode(canSales ? "sales" : "purchases");
    setRangeKey("thisFY");
    loadPayments();
  }, [isOpen, customer?.id, initialTab]);

  const { from, to } = useMemo(() => {
    if (rangeKey === "thisFY") return financialYearRange(0);
    if (rangeKey === "lastFY") return financialYearRange(-1);
    if (rangeKey === "all") return { from: "", to: "" };
    return custom;
  }, [rangeKey, custom]);

  const partyBills = useMemo(
    () => filterPartyBills(mode === "purchases" ? purchases : invoices, customer, mode).filter(ownsRecord),
    [mode, purchases, invoices, customer, ownsRecord]
  );
  const ledger = useMemo(
    () => {
      // Opening due typed in for this company: sales side for customers, purchase side for vendors
      const opensHere = mode === "purchases" ? partyType === "vendor" : partyType !== "vendor";
      return buildLedger({ bills: partyBills, payments, from, to, mode, openingDue: opensHere ? openingFor(customer, activeCompanyId) : 0 });
    },
    [partyBills, payments, from, to, mode, partyType, customer, activeCompanyId]
  );

  const paymentType = mode === "purchases" ? "PAYMENT_OUT" : "PAYMENT_IN";
  const rangePayments = useMemo(
    () => payments.filter((p) => {
      if (p.type !== paymentType) return false;
      const d = String(p.paymentDate || p.createdAt || "").slice(0, 10);
      return (!from || d >= from) && (!to || d <= to);
    }),
    [payments, paymentType, from, to]
  );
  const rangeReceived = rangePayments.filter((p) => p.status !== "VOID").reduce((s, p) => s + Number(p.amount || 0), 0);

  if (!customer) return null;

  const dateFormat = defaultCompany?.dateFormat || "DD-MM-YYYY";
  const fmt = (d) => (d ? formatAppDate(d, dateFormat) : "");
  const periodText = from || to ? `${fmt(from) || "Start"} to ${fmt(to) || "Today"}` : "All time";
  const fileName = `Ledger_${customer.name.replace(/[^A-Za-z0-9]+/g, "_")}_${from || "start"}_${to || "today"}.pdf`;

  const handleChanged = async () => {
    await loadPayments();
    if (mode === "purchases") await refetchPurchases();
    else await refetchInvoices();
  };

  const openRow = (row) => {
    if (row.kind === "payment") {
      setReceipt(row.record);
      return;
    }
    onClose();
    navigate(`/${mode === "purchases" ? "purchases" : "invoices"}/${row.record.id}`);
  };

  const runPdf = async (kind) => {
    if (!ledgerRef.current) return;
    setPdfBusy(kind);
    try {
      if (kind === "print") await openDocumentPdf(ledgerRef.current, fileName);
      else {
        const shareText = [
          `*${defaultCompany?.name || ""}*`,
          `Ledger — ${customer.name}`,
          `Period: ${periodText}`,
          `${mode === "purchases" ? "Bills" : "Billed"}: ${money(ledger.billed)} • ${mode === "purchases" ? "Paid" : "Received"}: ${money(ledger.settled)}`,
          `Closing balance (${balanceLabel(ledger.closing, mode)}): ${money(ledger.closing)}`,
          "Thank you for your business!"
        ].join("\n");
        await shareDocumentPdf(ledgerRef.current, fileName, {
          title: `Ledger — ${customer.name}`,
          text: shareText,
          phone: customer.mobile || customer.phone || ""
        });
      }
    } catch (err) {
      alert(err.message || "Could not create the PDF.");
    } finally {
      setPdfBusy("");
    }
  };

  const tabStyle = (active) => ({
    flex: 1,
    padding: "0.4rem 0.5rem",
    borderRadius: "calc(var(--radius-md) - 2px)",
    border: "none",
    cursor: "pointer",
    fontSize: "0.8rem",
    fontWeight: "700",
    background: active ? "var(--accent-blue, #38bdf8)" : "transparent",
    color: active ? "#0f172a" : "var(--text-muted)"
  });

  const rangeChips = (
    <div className="filter-pills-row" style={{ marginBottom: 0 }}>
      {RANGES.map((r) => (
        <button key={r.key} type="button" className={`filter-pill ${rangeKey === r.key ? "active" : ""}`} onClick={() => setRangeKey(r.key)}>
          {r.label}
        </button>
      ))}
    </div>
  );

  const customInputs = rangeKey === "custom" && (
    <div style={{ display: "flex", gap: "0.5rem" }}>
      <label style={{ flex: 1, fontSize: "0.72rem", color: "var(--text-dim)" }}>
        From
        <input type="date" className="form-control" value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} />
      </label>
      <label style={{ flex: 1, fontSize: "0.72rem", color: "var(--text-dim)" }}>
        To
        <input type="date" className="form-control" value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} />
      </label>
    </div>
  );

  const th = { padding: "5px 3px", borderBottom: "1.5px solid #0f172a", fontSize: "10.5px", textAlign: "left", fontWeight: 800 };
  const td = { padding: "5px 3px", borderBottom: "1px solid #e2e8f0", fontSize: "11px", verticalAlign: "top" };
  const num = { ...td, textAlign: "right", whiteSpace: "nowrap" };

  return (
    <>
      <BottomSheet isOpen={isOpen} onClose={onClose} title={`📒 ${customer.name}`} size="lg">
        <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
          {/* Party header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", marginTop: "-0.4rem" }}>
            <div style={{ fontSize: "0.8rem", color: "var(--text-dim)" }}>
              {customer.mobile && <>📞 {customer.mobile}</>}
              {customer.gstin && <> • GSTIN {customer.gstin}</>}
              {customer.city && <> • {customer.city}</>}
            </div>
            <Badge variant={ledger.allTimeBalance > 0.005 ? "warning" : "success"} style={{ fontSize: "0.8rem", padding: "0.25rem 0.6rem" }}>
              {balanceLabel(ledger.allTimeBalance, mode)} {money(ledger.allTimeBalance)}
            </Badge>
          </div>

          {canSales && canPurchases && (
            <div className="filter-pills-row" style={{ marginBottom: 0 }}>
              <button type="button" className={`filter-pill ${mode === "sales" ? "active" : ""}`} onClick={() => setMode("sales")}>🧾 Sales</button>
              <button type="button" className={`filter-pill ${mode === "purchases" ? "active" : ""}`} onClick={() => setMode("purchases")}>🛒 Purchases</button>
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: "flex", gap: "0.3rem", background: "var(--bg-card)", padding: "0.25rem", borderRadius: "var(--radius-md)", border: "1px solid var(--border-subtle)" }}>
            {TABS.map((t) => (
              <button key={t.key} type="button" style={tabStyle(tab === t.key)} onClick={() => setTab(t.key)}>
                {t.key === "invoices" && mode === "purchases" ? "Bills" : t.label}
              </button>
            ))}
          </div>

          {/* ---------------- LEDGER ---------------- */}
          {tab === "ledger" && (
            <>
              {rangeChips}
              {customInputs}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.5rem" }}>
                {[
                  [mode === "purchases" ? "Bills" : "Billed", ledger.billed, "var(--accent-blue)"],
                  [mode === "purchases" ? "Paid" : "Received", ledger.settled, "var(--success)"],
                  [balanceLabel(ledger.closing, mode), ledger.closing, ledger.closing > 0.005 ? "var(--warning)" : "var(--success)"]
                ].map(([label, value, color]) => (
                  <div key={label} style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", padding: "0.55rem", textAlign: "center" }}>
                    <div style={{ fontSize: "0.66rem", color: "var(--text-dim)", textTransform: "uppercase" }}>{label}</div>
                    <div style={{ fontSize: "0.95rem", fontWeight: "800", color }}>{money(value)}</div>
                  </div>
                ))}
              </div>

              {/* Printable ledger (also used for the PDF) */}
              <div
                ref={ledgerRef}
                style={{ background: "#ffffff", color: "#0f172a", borderRadius: "10px", padding: "0.6rem", maxHeight: "48vh", overflow: "auto", fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", borderBottom: "2px solid #0284c7", paddingBottom: "8px", marginBottom: "8px" }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: "15px" }}>{defaultCompany?.name || ""}</div>
                    <div style={{ fontSize: "10.5px", color: "#475569" }}>
                      {[defaultCompany?.addressLine1, defaultCompany?.city, defaultCompany?.pincode].filter(Boolean).join(", ")}
                    </div>
                    {defaultCompany?.gstin && <div style={{ fontSize: "10.5px", color: "#0284c7", fontWeight: 700 }}>GSTIN: {defaultCompany.gstin}</div>}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 800, fontSize: "13px", color: "#0284c7" }}>LEDGER</div>
                    <div style={{ fontSize: "10.5px", color: "#475569" }}>{periodText}</div>
                  </div>
                </div>
                <div style={{ fontSize: "11.5px", marginBottom: "8px" }}>
                  <span style={{ color: "#64748b" }}>{mode === "purchases" ? "Vendor" : "Customer"}: </span>
                  <strong>{customer.name}</strong>
                  {customer.city ? `, ${customer.city}` : ""}
                  {customer.gstin ? ` • GSTIN ${customer.gstin}` : ""}
                </div>

                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={th}>Date</th>
                      <th style={th}>Particulars</th>
                      <th style={{ ...th, textAlign: "right" }}>{mode === "purchases" ? "Bill" : "Debit"}</th>
                      <th style={{ ...th, textAlign: "right" }}>{mode === "purchases" ? "Paid" : "Credit"}</th>
                      <th style={{ ...th, textAlign: "right" }}>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={td}>{fmt(from)}</td>
                      <td style={{ ...td, fontWeight: 700 }}>Opening balance</td>
                      <td style={num} />
                      <td style={num} />
                      <td style={{ ...num, fontWeight: 700 }}>{money(ledger.opening)}{ledger.opening < -0.005 ? " Adv" : ""}</td>
                    </tr>
                    {ledger.rows.map((row, i) => (
                      <tr key={`${row.kind}-${row.record.id}-${i}`} onClick={() => openRow(row)} style={{ cursor: "pointer" }}>
                        <td style={{ ...td, whiteSpace: "nowrap" }}>{fmt(row.date)}</td>
                        <td style={td}>{rowParticulars(row, mode)}</td>
                        <td style={num}>{row.amount > 0 ? money(row.amount) : ""}</td>
                        <td style={{ ...num, color: "#047857" }}>{row.amount < 0 ? money(row.amount) : ""}</td>
                        <td style={{ ...num, fontWeight: 600 }}>{money(row.balance)}{row.balance < -0.005 ? " Adv" : ""}</td>
                      </tr>
                    ))}
                    {ledger.rows.length === 0 && (
                      <tr><td colSpan={5} style={{ ...td, textAlign: "center", color: "#64748b", padding: "14px" }}>No transactions in this period.</td></tr>
                    )}
                    <tr>
                      <td style={{ ...td, borderBottom: "none" }} />
                      <td style={{ ...td, fontWeight: 800, borderBottom: "none" }}>Closing balance ({balanceLabel(ledger.closing, mode)})</td>
                      <td style={{ ...num, fontWeight: 700, borderBottom: "none" }}>{money(ledger.billed)}</td>
                      <td style={{ ...num, fontWeight: 700, borderBottom: "none", color: "#047857" }}>{money(ledger.settled)}</td>
                      <td style={{ ...num, fontWeight: 800, borderBottom: "none" }}>{money(ledger.closing)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: "0.72rem", color: "var(--text-dim)", marginTop: "-0.4rem" }}>
                Tap a row to open the {mode === "purchases" ? "bill" : "invoice"} or receipt. Voided payments are not included.
              </div>

              <div style={{ display: "flex", gap: "0.75rem" }}>
                <Button type="button" variant="secondary" onClick={() => runPdf("print")} loading={pdfBusy === "print"} style={{ flex: 1 }}>
                  🖨️ Print
                </Button>
                <Button type="button" variant="primary" onClick={() => runPdf("share")} loading={pdfBusy === "share"} style={{ flex: 1 }}>
                  💬 Share on WhatsApp
                </Button>
              </div>
            </>
          )}

          {/* ---------------- PAYMENTS ---------------- */}
          {tab === "payments" && (
            <>
              {rangeChips}
              {customInputs}
              <div style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
                Total {mode === "purchases" ? "paid" : "received"}: <strong style={{ color: "var(--success)" }}>{money(rangeReceived)}</strong>
                {" • "}{rangePayments.filter((p) => p.status !== "VOID").length} {mode === "purchases" ? "payment(s)" : "receipt(s)"}
              </div>

              {loadingPayments ? (
                <div style={{ textAlign: "center", color: "var(--text-dim)", padding: "1.5rem" }}>⏳ Loading payments…</div>
              ) : rangePayments.length === 0 ? (
                <div style={{ textAlign: "center", color: "var(--text-dim)", padding: "1.5rem" }}>No payments in this period.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", maxHeight: "50vh", overflowY: "auto" }}>
                  {rangePayments.map((p) => {
                    const isVoid = p.status === "VOID";
                    const bills = (p.allocations || []).length;
                    return (
                      <div key={p.id} style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderLeft: `3px solid ${isVoid ? "var(--text-dim)" : "var(--success)"}`, borderRadius: "var(--radius-md)", padding: "0.65rem 0.85rem", opacity: isVoid ? 0.75 : 1 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem" }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ display: "flex", gap: "0.4rem", alignItems: "center", flexWrap: "wrap" }}>
                              <strong style={{ fontSize: "0.88rem" }}>{p.paymentNo}</strong>
                              <span style={{ fontSize: "0.75rem", color: "var(--text-dim)" }}>{p.paymentMode || "Cash"}</span>
                              {isVoid && <Badge variant="danger" style={{ fontSize: "0.65rem", padding: "1px 6px" }}>VOID</Badge>}
                              {!isVoid && p.editCount > 0 && <span style={{ fontSize: "0.7rem", color: "var(--text-dim)", fontStyle: "italic" }}>edited</span>}
                            </div>
                            <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", marginTop: "2px" }}>
                              📅 {fmt(p.paymentDate)}{bills > 0 && ` • ${bills} bill${bills === 1 ? "" : "s"} settled`}{p.reference && ` • Ref: ${p.reference}`}
                            </div>
                            {isVoid && p.voidReason && <div style={{ fontSize: "0.72rem", color: "var(--danger)" }}>Voided: {p.voidReason}</div>}
                          </div>
                          <div style={{ fontWeight: 800, color: isVoid ? "var(--text-dim)" : "var(--success)", textDecoration: isVoid ? "line-through" : "none", whiteSpace: "nowrap" }}>
                            {money(p.amount)}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: "0.35rem", justifyContent: "flex-end", marginTop: "0.45rem" }}>
                          <Button type="button" size="sm" variant="secondary" onClick={() => setReceipt(p)} style={{ fontSize: "0.72rem", padding: "2px 8px" }}>📄 Receipt</Button>
                          {!isVoid && (
                            <>
                              <Button type="button" size="sm" variant="secondary" onClick={() => setEditPayment(p)} style={{ fontSize: "0.72rem", padding: "2px 8px" }}>✏️ Edit</Button>
                              <Button type="button" size="sm" variant="secondary" onClick={() => setVoidingPayment(p)} style={{ fontSize: "0.72rem", padding: "2px 8px", color: "var(--danger)" }}>🚫 Void</Button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* ---------------- INVOICES / BILLS ---------------- */}
          {tab === "invoices" && mode === "sales" && <CustomerInvoicesPanel customer={customer} />}
          {tab === "invoices" && mode === "purchases" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", maxHeight: "55vh", overflowY: "auto" }}>
              {partyBills.length === 0 && <div style={{ textAlign: "center", color: "var(--text-dim)", padding: "1.5rem" }}>No purchase bills for this vendor.</div>}
              {partyBills.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => { onClose(); navigate(`/purchases/${b.id}`); }}
                  style={{ display: "flex", justifyContent: "space-between", textAlign: "left", background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", padding: "0.65rem 0.85rem", color: "var(--text-main)", cursor: "pointer" }}
                >
                  <span>
                    <strong>{b.vendorBillNo || "No Bill #"}</strong>
                    <span style={{ display: "block", fontSize: "0.75rem", color: "var(--text-dim)" }}>📅 {fmt(b.billDate)}</span>
                  </span>
                  <span style={{ textAlign: "right" }}>
                    <strong>{money(b.grandTotal)}</strong>
                    <span style={{ display: "block", fontSize: "0.72rem", color: Number(b.balance || 0) > 0 ? "var(--warning)" : "var(--success)" }}>{b.paymentStatus || "Unpaid"}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </BottomSheet>

      <PaymentReceiptModal isOpen={!!receipt} onClose={() => setReceipt(null)} payment={receipt} />
      <PaymentCorrectionHost
        editPayment={editPayment}
        voidingPayment={voidingPayment}
        onCloseEdit={() => setEditPayment(null)}
        onCloseVoid={() => setVoidingPayment(null)}
        onChanged={handleChanged}
      />
    </>
  );
}

export default CustomerLedgerModal;
